import { Phone, Shield, Bug, CheckCircle, Clock, Biohazard, Calendar, Users, BadgeCheck, Award } from "lucide-react";
import WhatsAppIcon from "@/components/ads/WhatsAppIcon";
import { useTrackedCTA } from "@/hooks/useTrackedCTA";
import AdsLandingLayout from "@/components/ads/AdsLandingLayout";

const badges = [
  "Mais de 20 anos de mercado",
  "Mais de 50.000 clientes atendidos",
  "Resultado satisfatório ou seu dinheiro de volta",
];

const problemas = [
  { icon: Bug, title: "Baratas e insetos escondidos", text: "Focos em ralos, armários, frestas, caixas e áreas úmidas." },
  { icon: Biohazard, title: "Risco de contaminação", text: "Pragas transmitem doenças e contaminam alimentos e superfícies." },
  { icon: Clock, title: "Infestação voltando rápido", text: "Sem aplicação técnica, a colônia retorna em poucos dias." },
];

const beneficios = [
  "Produto exclusivo",
  "Não desaloja insetos de forma agressiva",
  "Ajuda a evitar atração de escorpiões",
  "Aplicação técnica em pontos estratégicos",
  "Seguro para pets e crianças após a secagem",
  "Garantia de resultado",
];

const passos = [
  "Você chama no WhatsApp e informa o tipo de praga",
  "A equipe orienta e agenda o serviço",
  "O técnico aplica nos pontos estratégicos",
  "Você recebe orientações e acompanhamento da garantia",
];

const provas = [
  { icon: Calendar, text: "Mais de 20 anos de mercado" },
  { icon: Users, text: "Mais de 50.000 clientes atendidos" },
  { icon: BadgeCheck, text: "Produtos aprovados pela ANVISA" },
  { icon: Award, text: "Garantia de resultado satisfatório ou seu dinheiro de volta" },
];

const faqs = [
  {
    q: "Quanto custa uma dedetização em Brasília?",
    a: "O valor depende do tamanho do imóvel, tipo de praga e nível de infestação. O orçamento é gratuito pelo WhatsApp.",
  },
  {
    q: "A dedetização é segura para pets e crianças?",
    a: "Sim, quando aplicada corretamente por profissionais. Os produtos são aprovados pela ANVISA e seguros após a secagem, seguindo as orientações do técnico.",
  },
  {
    q: "Preciso sair de casa?",
    a: "Na maioria dos casos, não. A orientação pode variar conforme o ambiente e o tipo de aplicação.",
  },
  {
    q: "Quanto tempo dura o efeito?",
    a: "Em média de 3 a 6 meses, dependendo do ambiente, tipo de praga e condições do imóvel.",
  },
  {
    q: "A dedetização atrai escorpiões?",
    a: "O método da USA foi pensado para não desalojar insetos de forma agressiva, reduzindo esse risco.",
  },
];

const DedetizacaoUrgente = () => {
  const { handleWhatsAppClick, handlePhoneClick, whatsAppUrl, phone } = useTrackedCTA();

  return (
    <AdsLandingLayout
        title="Dedetização em Brasília com Atendimento Rápido | Dedetizadora USA"
        description="Dedetização profissional em Brasília com produto exclusivo, atendimento rápido, segurança para pets e garantia de resultado. Solicite orçamento grátis pelo WhatsApp."
      >
        {/* Hero */}
        <section id="hero" className="relative text-white py-12 md:py-20 px-4 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="/images/hero-800.webp"
              srcSet="/images/hero-800.webp 800w, /images/hero-1280.webp 1280w, /images/hero.webp 1920w"
              sizes="100vw"
              alt="Profissional da Dedetizadora USA realizando controle de pragas"
              className="w-full h-full object-cover object-top"
              width={1920}
              height={1080}
              fetchPriority="high"
              decoding="async"
            />
            <div
              className="absolute inset-0"
              style={{ background: "linear-gradient(135deg, rgba(29,138,79,0.92), rgba(13,74,42,0.95))" }}
              aria-hidden="true"
            />
            <div
              className="absolute inset-0 opacity-60"
              style={{ background: "radial-gradient(circle at 30% 20%, #22c46a33, transparent 60%)" }}
              aria-hidden="true"
            />
          </div>
          <div className="relative max-w-4xl mx-auto text-center">
            <h1 className="text-3xl md:text-5xl font-bold mb-4 leading-tight">
              Dedetização em Brasília com atendimento rápido e garantia de resultado
            </h1>
            <p className="text-base md:text-xl mb-6 opacity-95 max-w-3xl mx-auto">
              Elimine baratas, formigas, aranhas e outros insetos com produto exclusivo que age em
              cadeia, sem desalojar pragas e sem atrair escorpiões.
            </p>
            <ul className="flex flex-wrap justify-center gap-2 mb-8">
              {badges.map((b) => (
                <li
                  key={b}
                  className="bg-white/10 border border-white/30 rounded-full px-3 py-1 text-xs md:text-sm"
                >
                  {b}
                </li>
              ))}
            </ul>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href={whatsAppUrl}
                onClick={handleWhatsAppClick("ads_urgente_hero_whatsapp")}
                aria-label="Chamar no WhatsApp agora"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-bold animate-cta-attention"
                style={{ backgroundColor: "#22c46a", color: "#0a1f14", boxShadow: "0 12px 40px -10px #22c46a99" }}
              >
                <WhatsAppIcon size={22} />
                Chamar no WhatsApp Agora
              </a>
              <a
                href={phone}
                onClick={handlePhoneClick("ads_urgente_hero_phone")}
                aria-label="Ligar agora"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-bold border-2 border-white/80 text-white hover:bg-white/10 animate-cta-attention"
              >
                <Phone className="w-5 h-5" />
                Ligar Agora
              </a>
            </div>
          </div>
        </section>

        {/* Problema */}
        <section id="problema" className="py-12 md:py-16 px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-3 text-center">
              Viu barata em casa? O problema pode ser maior do que parece.
            </h2>
            <p className="text-muted-foreground text-center max-w-3xl mx-auto mb-8">
              Quando uma barata aparece, normalmente já existem focos escondidos em ralos, armários,
              frestas, caixas e áreas úmidas. A limpeza comum ajuda, mas não elimina a colônia. Por
              isso a dedetização profissional precisa agir onde a praga se abriga.
            </p>
            <div className="grid md:grid-cols-3 gap-4">
              {problemas.map(({ icon: Icon, title, text }) => (
                <div key={title} className="bg-card border border-border rounded-lg p-5">
                  <Icon className="w-8 h-8 text-primary mb-3" />
                  <h3 className="font-semibold text-card-foreground mb-1">{title}</h3>
                  <p className="text-sm text-muted-foreground">{text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Mecanismo único */}
        <section id="metodo" className="bg-muted py-12 md:py-16 px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-3 text-center">
              O diferencial está no método de aplicação
            </h2>
            <p className="text-muted-foreground text-center max-w-3xl mx-auto mb-8">
              A Dedetizadora USA utiliza um inseticida profissional de uso exclusivo que age em
              cadeia. O objetivo é contaminar a colônia sem provocar desalojamento intenso,
              reduzindo o risco de atrair escorpiões e lacraias.
            </p>
            <ul className="grid sm:grid-cols-2 gap-3 max-w-2xl mx-auto">
              {beneficios.map((b) => (
                <li key={b} className="flex items-start gap-2 bg-card border border-border rounded-lg p-3">
                  <CheckCircle className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                  <span className="text-sm text-card-foreground">{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Processo */}
        <section id="como-funciona" className="py-12 md:py-16 px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-8 text-center">
              Como funciona o atendimento
            </h2>
            <ol className="grid sm:grid-cols-2 gap-4">
              {passos.map((p, i) => (
                <li key={p} className="flex gap-3 bg-card border border-border rounded-lg p-4">
                  <span className="flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground font-bold shrink-0">
                    {i + 1}
                  </span>
                  <span className="text-card-foreground">{p}</span>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* Prova / Confiança */}
        <section id="prova" className="bg-primary/5 py-12 md:py-16 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-8">
              Uma empresa de controle de pragas validada por milhares de clientes
            </h2>
            <ul className="grid sm:grid-cols-2 gap-3 max-w-2xl mx-auto text-left">
              {provas.map(({ icon: Icon, text }) => (
                <li key={text} className="flex items-start gap-2">
                  <Icon className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                  <span className="text-foreground">{text}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="py-12 md:py-16 px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-8 text-center">
              Perguntas frequentes
            </h2>
            <div className="space-y-3">
              {faqs.map((f) => (
                <details key={f.q} className="bg-card border border-border rounded-lg p-4 group">
                  <summary className="font-semibold text-card-foreground cursor-pointer list-none flex justify-between items-center">
                    {f.q}
                    <span className="text-primary group-open:rotate-45 transition-transform">+</span>
                  </summary>
                  <p className="mt-3 text-sm text-muted-foreground">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* CTA final */}
        <section id="cta-final" className="relative text-white py-16 md:py-20 px-4 text-center overflow-hidden">
          <div
            className="absolute inset-0"
            style={{ background: "linear-gradient(135deg, #1d8a4f, #0d4a2a)" }}
            aria-hidden="true"
          />
          <div
            className="absolute inset-0 opacity-70"
            style={{ background: "radial-gradient(circle at 70% 30%, #22c46a33, transparent 60%)" }}
            aria-hidden="true"
          />
          <div className="relative max-w-3xl mx-auto">
            <CheckCircle className="w-10 h-10 mx-auto mb-4" style={{ color: "#22c46a" }} />
            <h2 className="text-2xl md:text-4xl font-bold mb-3">Livre-se das pragas hoje mesmo</h2>
            <p className="mb-6 opacity-95 max-w-2xl mx-auto">
              Solicite um orçamento gratuito e receba orientação pelo WhatsApp.
            </p>
            <a
              href={whatsAppUrl}
              onClick={handleWhatsAppClick("ads_urgente_final_whatsapp")}
              aria-label="Solicitar orçamento no WhatsApp"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full font-bold animate-cta-attention"
              style={{ backgroundColor: "#22c46a", color: "#0a1f14", boxShadow: "0 12px 40px -10px #22c46a99" }}
            >
              <WhatsAppIcon size={22} />
              Solicitar Orçamento no WhatsApp
            </a>
          </div>
        </section>
      </AdsLandingLayout>
  );
};

export default DedetizacaoUrgente;
